import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../pages/Navbar';
import Footer from '../pages/Footer';
import '../pages/Shop/shop.css';

const Bestsellers = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Base URL for images
  const BASE_URL = 'https://evercorenutrition.com';

  useEffect(() => {
    fetchBestsellers();
  }, []);

  const fetchBestsellers = async () => {
    setLoading(true);
    try {
      // Fetch both products and combos in parallel
      const [productsResponse, combosResponse] = await Promise.all([
        fetch('https://evercorenutrition.com/ev_admin/api/productslist/'),
        fetch('https://evercorenutrition.com/ev_admin/api/combos/')
      ]);

      const productsData = await productsResponse.json();
      const combosData = await combosResponse.json();

      let bestsellerItems = [];

      // 1. Filter products with is_bestseller: true in variants
      if (productsData.results && productsData.results.length > 0) {
        productsData.results.forEach(product => {
          // Check if product has any variant marked as bestseller
          const bestsellerVariants = (product.variants || []).filter(variant => variant.is_bestseller === true);
          
          if (bestsellerVariants.length > 0) {
            // For each bestseller variant, create a separate item
            bestsellerVariants.forEach(variant => {
              bestsellerItems.push({
                id: product.id,
                type: 'product',
                name: product.name,
                image: variant.main_image || variant.image_url || product.main_image,
                price: variant.price,
                mrp: variant.mrp,
                discount: variant.discount,
                weight: variant.weight_display || `${variant.weight_value}${variant.weight_unit}`,
                average_rating: product.average_rating || 0,
                total_reviews: product.total_reviews || 0,
                stock: variant.qty > 0,
                variant_id: variant.id,
                variant_name: variant.flavour,
                variant_weight: variant.weight_display,
                is_bestseller: true,
                batch_no: variant.batch_no
              });
            });
          }
        });
      }

      // 2. Filter combos with is_bestseller: true
      if (combosData.results && combosData.results.length > 0) {
        const bestsellerCombos = combosData.results.filter(combo => 
          combo.is_bestseller === true
        );

        // Map combos to a unified format
        bestsellerCombos.forEach(combo => {
          bestsellerItems.push({
            id: combo.id,
            type: 'combo',
            name: combo.name,
            image: combo.image_url,
            price: parseFloat(combo.selling_price),
            mrp: parseFloat(combo.mrp),
            discount: parseFloat(combo.discount),
            weight: `${combo.no_of_packs} packs`,
            average_rating: 0,
            total_reviews: 0,
            stock: combo.status === 'active',
            combo_id: combo.combo_id,
            is_bestseller: true
          });
        });
      }

      // Sort: Combos first, then products with higher discount
      const sortedBestsellers = bestsellerItems.sort((a, b) => {
        // Combos first
        if (a.type === 'combo' && b.type !== 'combo') return -1;
        if (a.type !== 'combo' && b.type === 'combo') return 1;
        // Then sort by discount
        return (b.discount || 0) - (a.discount || 0);
      });

      setProducts(sortedBestsellers);
     
      
    } catch (error) {
      console.error('Error fetching bestsellers:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get full product/combo image URL
  const getImageUrl = (item) => {
    let imagePath = null;
    
    if (item.image) {
      imagePath = item.image;
    } else if (item.main_image) {
      imagePath = item.main_image;
    } else if (item.images && item.images[0]) {
      imagePath = item.images[0].image_url;
    }
    
    if (imagePath) {
      if (imagePath.startsWith('http')) {
        return imagePath;
      }
      return `${BASE_URL}${imagePath}`;
    }
    
    return '/assets/images/placeholder.png';
  };

  // Function to render star rating
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    let stars = '';
    
    for (let i = 0; i < fullStars; i++) {
      stars += '★';
    }
    
    if (hasHalfStar) {
      stars += '½';
    }
    
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars += '☆';
    }
    
    return stars;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      
      <section className="section-b-space shop-section">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              {/* Header Section */}
              <div className="text-center mb-5">
                <h1 className="display-4 fw-bold" style={{ color: '#333' }}>
                  Best Sellers
                </h1>
                <p className="lead" style={{ color: '#666' }}>
                  Our most popular and trusted nutrition products & combos
                </p>
                <div className="cta-divider" style={{ width: '100px', margin: '20px auto', height: '3px', background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }}></div>
              </div>

              {/* Products Grid */}
              <div className="row" style={{ margin: 0 }}>
                <div className="col-12" style={{ padding: 0 }}>
                  <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: '1fr', width: '100%', margin: 0, padding: 0 }}>
                    {products.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', gridColumn: '1/-1' }}>
                        <h4>No bestsellers found</h4>
                        <p className="text-muted">Check back soon for our best sellers!</p>
                      </div>
                    ) : (
                      products.map((item, index) => {
                        const inStock = item.stock;
                        const averageRating = item.average_rating || 0;
                        const discount = item.discount || 0;
                        const imageUrl = getImageUrl(item);
                        const reviewCount = item.total_reviews || 0;
                        const detailUrl = item.type === 'combo' ? `/combo/${item.id}` : `/product/${item.id}`;

                        return (
                          <div key={`${item.type}-${item.id}-${item.variant_id || index}`} style={{ width: '100%', padding: 0, margin: 0, boxShadow: "1px 2px 15px #868585", borderRadius: "10px" }}>
                            <div
                              style={{
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                backgroundColor: '#f0f0f0',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                cursor: 'pointer',
                                position: 'relative',
                                opacity: inStock ? 1 : 0.8
                              }}
                              onMouseEnter={(e) => {
                                if (window.innerWidth > 768 && inStock) {
                                  e.currentTarget.style.transform = 'translateY(-5px)';
                                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                              }}
                            >
                              {/* Stock Status Badge */}
                              {!inStock && (
                                <div style={{
                                  position: 'absolute',
                                  top: '10px',
                                  left: '10px',
                                  backgroundColor: '#ff4444',
                                  color: 'white',
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  zIndex: 3,
                                  boxShadow: '0 2px 8px rgba(255,68,68,0.3)'
                                }}>
                                  ⏰ Out of Stock
                                </div>
                              )}

                              {/* Type Badge (Combo/Product) */}
                              <div style={{
                                position: 'absolute',
                                top: '10px',
                                left: '10px',
                                backgroundColor: item.type === 'combo' ? '#9b59b6' : '#3498db',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '11px',
                                fontWeight: '600',
                                zIndex: 3,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                textTransform: 'uppercase'
                              }}>
                                {item.type === 'combo' ? 'COMBO' : 'PRODUCT'}
                              </div>

                              {/* Product Image */}
                              <div style={{ 
                                position: 'relative', 
                                width: '100%',
                                aspectRatio: '1/1',
                                overflow: 'hidden', 
                                backgroundColor: '#f0f0f0' 
                              }}>
                                <a href={detailUrl} style={{ display: 'block', width: '100%', height: '100%' }}>
                                  <img
                                    src={imageUrl}
                                    alt={item.name}
                                    onError={(e) => {
                                      e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
                                    }}
                                    style={{ 
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      padding: '0',
                                      transition: 'transform 0.3s ease',
                                      filter: inStock ? 'none' : 'grayscale(50%)'
                                    }}
                                    onMouseEnter={(e) => { if (window.innerWidth > 768 && inStock) e.currentTarget.style.transform = 'scale(1.05)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                  />
                                </a>
                              </div>

                              {/* Content Section */}
                              <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                {/* Product Name */}
                                <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: '0 0 8px 0', color: '#333', lineHeight: '1.4' }}>
                                  <a href={detailUrl} style={{ color: '#333', textDecoration: 'none' }}>
                                    {item.name}
                                  </a>
                                </h3>

                                {/* Variant Info (for products) */}
                                {item.type === 'product' && item.variant_name && (
                                  <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 5px 0' }}>
                                    {item.variant_name} - {item.variant_weight}
                                    {/* {item.batch_no && (
                                      <span style={{ fontSize: '0.7rem', color: '#999', marginLeft: '5px' }}>
                                        (Batch: {item.batch_no})
                                      </span>
                                    )} */}
                                  </p>
                                )}

                                {/* Rating - Only for products with reviews */}
                                {item.type === 'product' && reviewCount > 0 && (
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                                    <div style={{ color: '#ffc107', fontSize: '1rem', letterSpacing: '2px', marginRight: '5px' }}>
                                      {renderStars(averageRating)}
                                    </div>
                                    <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '5px' }}>
                                      ({averageRating > 0 ? averageRating.toFixed(1) : '0.0'})
                                    </span>
                                    <span style={{ fontSize: '0.85rem', color: '#999', marginLeft: '5px' }}>
                                      ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                                    </span>
                                  </div>
                                )}

                                {/* Pack Size / Weight */}
                                {item.weight && (
                                  <p style={{ fontSize: '0.9rem', color: '#666', margin: '0 0 10px 0' }}>
                                    {item.weight}
                                  </p>
                                )}

                                {/* Price Section */}
                                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px' }}>
                                  <span style={{ fontSize: '1.3rem', fontWeight: '700', color: '#333' }}>
                                    ₹{parseFloat(item.price || 0).toLocaleString('en-IN')}
                                  </span>
                                  {item.mrp > item.price && (
                                    <span style={{ fontSize: '0.9rem', color: '#999', textDecoration: 'line-through', marginLeft: '8px' }}>
                                      ₹{parseFloat(item.mrp || 0).toLocaleString('en-IN')}
                                    </span>
                                  )}
                                  {discount > 0 && (
                                    <span style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '3px',
                                      fontSize: '0.75rem',
                                      color: '#fff',
                                      backgroundColor: '#e53935',
                                      padding: '4px 10px',
                                      borderRadius: '20px',
                                      marginLeft: '4px',
                                      fontWeight: '700',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {Math.round(discount)}% OFF
                                    </span>
                                  )}
                                </div>

                                {/* View Product/Combo Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(detailUrl);
                                  }}
                                  disabled={!inStock}
                                  style={{ 
                                    width: '100%',
                                    padding: '10px 12px', 
                                    backgroundColor: !inStock ? '#999' : '#000', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '6px', 
                                    fontSize: '0.9rem', 
                                    fontWeight: '600', 
                                    cursor: !inStock ? 'not-allowed' : 'pointer', 
                                    transition: 'background-color 0.2s ease',
                                    opacity: !inStock ? 0.7 : 1,
                                    marginTop: 'auto'
                                  }}
                                  onMouseEnter={(e) => { 
                                    if (window.innerWidth > 768 && inStock) {
                                      e.currentTarget.style.backgroundColor = '#333'; 
                                    }
                                  }}
                                  onMouseLeave={(e) => { 
                                    if (inStock) {
                                      e.currentTarget.style.backgroundColor = '#000'; 
                                    }
                                  }}
                                >
                                  {!inStock ? 'Out of Stock' : (item.type === 'combo' ? 'View Combo' : 'View Product')}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (min-width: 768px) {
          div[style*="display: grid"][style*="gap: 20px"] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (min-width: 1200px) {
          div[style*="display: grid"][style*="gap: 20px"] {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }
        .container-fluid-lg {
          padding-left: 15px !important;
          padding-right: 15px !important;
        }
        @media (min-width: 768px) {
          .container-fluid-lg { padding-left: 20px !important; padding-right: 20px !important; }
        }
        @media (min-width: 1200px) {
          .container-fluid-lg { padding-left: 30px !important; padding-right: 30px !important; }
        }
        .row { margin-left: 0 !important; margin-right: 0 !important; }
        .col-12 { padding-left: 0 !important; padding-right: 0 !important; }
      `}</style>

      <Footer />
    </>
  );
};

export default Bestsellers;